/**
 * toolRegistry — default ToolRegistry for the zelari-code CLI.
 *
 * Wires the built-in tools (filesystem read/write/edit + bash + grep/list +
 * show_diff) into a ToolRegistry instance that the AgentHarness can
 * hand to the provider via `tools: registry.toOpenAITools()` + `toolRegistry: registry`.
 *
 * Task A1 of AnathemaCoder v3-A: enable the existing tool pipeline.
 * Task A2 of v3-A: wrap each tool with the safety layer (sandbox path,
 * shell blocklist, audit log).
 * v0.4.0: added show_diff + apply_diff + recursive grep_content.
 *
 * @see docs/plans/ (v3 plan, 2026-06-29) (Tasks A1 + A2)
 * @see docs/plans/2026-07-01-v0-4-0-fix-audit.md (v0.4.0 scope)
 */
import { ToolRegistry } from '@zelari/core/harness/tools/registry';
import {
  readFileTool,
  writeFileTool,
} from '@zelari/core/harness/tools/builtin/filesystem';
import { editTool } from '@zelari/core/harness/tools/builtin/edit';
import { createBashTool, type BashSpawnSeam } from '@zelari/core/harness/tools/builtin/shell';
import { grepContentTool } from '@zelari/core/harness/tools/builtin/search';
import { listFilesTool } from '@zelari/core/harness/tools/builtin/listFiles';
import { showDiffTool } from '@zelari/core/harness/tools/builtin/diff';
import { fetchUrlTool, webSearchTool } from '@zelari/core/harness/tools/builtin/web';
import { resolveSandboxedPath, SandboxViolationError, verifyContainment } from './safety/sandboxPath.js';
import { createTaskTouchGuard } from './workspace/taskTouchGuard.js';
import { runTaskStalenessCheck } from './workspace/taskStaleness.js';
import { assertShellAllowed, ShellBlockedError } from './safety/shellBlocklist.js';
import { AuditLogger } from './safety/auditLogger.js';
import {
  DEFAULT_PROVIDERS,
  formatDiagnostics,
  runDiagnosticsForFile,
  type Diagnostic,
  type Runner,
} from './diagnostics/engine.js';
import { existsSync } from 'node:fs';
import path from 'node:path';
import type { BrainEvent } from '@zelari/core/shared/events';
import {
  createTaskTool,
  permissionsForTaskAgent,
  type TaskAgentKind,
  type TaskToolDeps,
} from './tools/taskTool.js';
import { createKrakenSelectTool } from './tools/krakenSelectTool.js';
import { createAskUserTool, type AskUserHandler } from './tools/askUser.js';
import { createSkillTool } from './tools/skillTool.js';
import { createTodoReadTool, createTodoWriteTool } from './tools/todoTools.js';
import {
  createPlanTaskTools,
  type PlanTaskEventSink,
} from './tools/planTaskTools.js';
import { createInspectCommandTool } from './tools/inspectCommand.js';
import { createExecProcessTool } from './tools/execProcess.js';
import { createObserveBatchTool } from './tools/observeBatch.js';
import { createRetrieveObservationTool } from './tools/retrieveObservation.js';
import { createLspTools } from './lsp/tools.js';
import { getSharedLspManager, type LspProvider } from './lsp/manager.js';
import { createAstTools } from './ast/tools.js';
import { createSemanticTool } from './semantic/tools.js';
import { createBrowserTool } from './browser/tools.js';
import { createScreenshotTool } from './tools/screenshotTool.js';
import { createSshTools } from './ssh/tools.js';
import { createWorldModelTools } from './workspace/worldModel.js';
import {
  providerFromEnv,
  providerConfigFor,
} from './provider/openai-compatible.js';
import { buildProviderStream } from './provider/resolveStream.js';
import { getKrakenVerifierOverride } from './providerConfig.js';
import { wrapWithHeadlessFailover } from './crossProviderFailover.js';
import { PROVIDERS, type ProviderName } from './keyStore.js';
import {
  activePermissionPreset,
  defaultPermissionPolicy,
  intersectPermissionPolicy,
  isSessionGranted,
  resolveToolPermission,
  type PermissionAskHandler,
  type PermissionPolicy,
} from './safety/toolPermissions.js';
// v2.32 (S5): destructive-shape escalation at the same choke-point.
import { destructiveCommandHit } from './safety/destructiveCommands.js';
import {
  provenanceAppliesTo,
  provenanceMatchIn,
  recordResultForProvenance,
} from './safety/provenance.js';
import { createDefaultLifecycleHooks, HOOKS_FAILURE_ENV, resolveHookFailureMode } from './safety/lifecycleHooks.js';
// ADR-0033 (t76): post-write AST gate with auto-revert on the write path.
import { wrapWithAstGate } from './safety/astGate.js';
// t30 (Pilastro C): ExtensionAPI seam — extension tools ride the SAME
// wrapWithPermissions path as builtins; onPreToolUse handlers ride the t22
// failure semantics (resolveHookFailureMode, same env override as hooks).
import { extensionToolToDefinition, withExtensionPreToolUse } from './extensions/extensionToolWiring.js';
import {
  agentLayersFor,
  emptyPolicySet,
  loadPolicySet,
  mergeRuleEffect,
  PolicyLoadError,
  type LayeredPolicyRuleSet,
  type PolicyPrecedence,
} from './safety/policyEngine.js';
import { activePolicyLoadMode } from './safety/policyLoadMode.js';
import { intersectEffects, matchAgentPolicyRuleLayered } from './safety/policyLayers.js';
// t22: the TaskContract compiles into a NON-OVERRIDABLE restrict-only layer.
import { matchContractCapabilityRule } from './kraken/contractCompiler.js';
import { describeResourceClaim, matchResourceClaimLayered, resourceClaimsFor, resolveClaimsVerdict } from './safety/resourceClaims.js';
// v2.17 (t28): OS jail (Pilastro A) — the ONLY spawn choke-point for exec/bash.
import {
  buildJailSpec,
  jailAdvisoryNotice,
  jailAvailability,
  jailDenyReason,
  networkSpecFromClaimHosts,
  spawnJailed,
  type JailNetwork,
  type JailSpec,
} from './safety/osJail.js';
import { withResultCache } from './toolResultCache.js';
import { inspectCommand } from './safety/selfKillGuard.js';
import type { ExtensionRegistry, LifecycleHookRunner } from '@zelari/core/harness';
import type {
  ToolDefinition,
  TypedResult,
  ToolContext,
  ToolPermission,
} from '@zelari/core/harness/tools/toolTypes';
import { typedErr } from '@zelari/core/harness/tools/toolTypes';
import { cliToolToEnhanced, registerCustomTool } from '@zelari/core/skills';
import type { EnhancedToolDefinition } from '@zelari/core/skills';
import type { MemoryService } from '@zelari/core/memory';

export interface BuiltinToolSummary {
  /** Tool name as registered. */
  name: string;
  /** Tool description. */
  description: string;
  /** Required permission (read | write | execute). */
  permissions: readonly string[];
}

export interface CreateRegistryOptions {
  /** Sandbox root. Defaults to process.cwd(). */
  root?: string;
  /** Audit logger instance. If omitted, creates a default file-backed one. */
  audit?: AuditLogger;
  /** Session id used in audit entries. */
  sessionId?: string;
  /**
   * Enable the diagnostics loop (fast file-scoped checker appending
   * errors to the result): post-edit on write_file/edit_file/apply_diff,
   * and — since 2.21 (t31) — post-execute on bash/exec_process over the
   * source paths the invocation claims. Defaults to true unless
   * `ZELARI_DIAGNOSTICS=0` is set.
   */
  diagnostics?: boolean;
  /** Inject the diagnostics process runner (tests). Defaults to real spawn. */
  diagnosticsRunner?: Runner;
  /**
   * Read-only registry: register only observe tools (read/list/grep/show_diff/
   * fetch/web) and omit write/edit/apply_diff/bash + the `task` tool. Used to
   * build the isolated, non-recursive registry each sub-agent runs with.
   */
  readOnly?: boolean;
  /** Register the `task` sub-agent tool (default true unless readOnly). */
  enableTask?: boolean;
  /**
   * Context-pressure band for the skill-catalog gate: under `high` pressure,
   * high-cost skills stay loadable by name but are hidden from the advertised
   * catalog (budget-aware retrieval, T4 follow-up). Default: no gating.
   */
  pressureBand?: 'low' | 'medium' | 'high';
  /**
   * Fase 1 (ADR-0020): anchor the sub-agents spawned by `task` to this
   * provider/model (the resolved provider/model of the CURRENT turn).
   * Without this, tentacles silently fall back to the persisted
   * provider.json default, which can diverge from what the user selected.
   */
  subAgentProvider?: string;
  subAgentModel?: string;
  /**
   * Register the plan-safe explore-only `task` tool when planMode is on
   * (default true). Set false to restore the pre-ADR-0020 behaviour of
   * omitting `task` entirely from the plan registry.
   */
  planExploreTask?: boolean;
  /** Sink for tentacle activity events (Frontier plan §37); forwarded to the `task` tool. */
  onTentacleEvent?: (ev: BrainEvent) => void;
  /**
   * Fase 4 (ADR-0020): register `kraken_select` on the PARENT Kraken
   * registry. Callers set it only for kraken runs with the alpha
   * selection flag on; tentacle profiles never pass it, so sub-agents
   * can never nest a selection call.
   */
  krakenSelect?: boolean;
  /**
   * LSP navigation provider. Omit to use the shared, real language-server
   * manager; pass a fake in tests; pass `null` to disable the LSP tools.
   */
  lspProvider?: LspProvider | null;
  /**
   * v1.8.0 plan phase: omit mutating builtins (write/edit/bash/apply_diff).
   * Fase 1 (ADR-0020): the `task` tool now STAYS available in plan,
   * restricted to explore-only (read-only) tentacles — see planExploreTask.
   * Workspace plan tools can still be registered by the
   * caller. Equivalent to a soft read-only for project files.
   */
  planMode?: boolean;
  /**
   * Gauntlet conductor parent (P2): keep `task` so the host can fan out
   * builder/critic tentacles, but omit write/edit/bash and other mutators
   * so the lead cannot eat the work itself.
   */
  gauntletParent?: boolean;
  /**
   * Interactive ask_user tool (Grok-style). When provided, the tool blocks
   * the harness until the UI resolves. Omit for headless / readOnly subagents.
   */
  onAskUser?: AskUserHandler;
  /**
   * Permission policy (allow/ask/deny by category). Defaults from env via
   * {@link defaultPermissionPolicy}.
   */
  permissionPolicy?: PermissionPolicy;
  /**
   * Interactive approval when a tool resolves to "ask". If omitted and action
   * is ask (and not auto), the tool is denied with a clear message.
   */
  onPermissionAsk?: PermissionAskHandler;
  /**
   * P0.5 policy engine: the agent identity this registry runs as — 'lead'
   * (the default when omitted) for the main registry, or the sub-agent kind
   * ('explore' | 'general' | 'verify') for tentacle registries. Selects the
   * per-agent rule lists from `.zelari/policy.json` (project) and
   * `~/.zelari/policy.json` (global); a missing/broken file or
   * ZELARI_POLICY=0 yields no rules. createKrakenSubAgentContextFactory
   * passes the tentacle's kind here — that is how per-AGENT rules reach
   * sub-agents without threading identity through ToolContext.
   */
  policyAgent?: string;
  /**
   * Sub-agent tool profile:
   * - full: default parent registry
   * - explore: read-only observe tools
   * - verify: observe + bash (no writes, no task)
   * - general: full mutators but no nested task
   */
  profile?: 'full' | 'explore' | 'verify' | 'general';
  /** Register the lazy `skill` tool (default true unless readOnly/sub profile). */
  enableSkill?: boolean;
  /** Register session todo_write/todo_read (default true for full/plan parent). */
  enableTodos?: boolean;
  /**
   * Register the durable workspace plan-task tools (task_create/task_update/
   * task_list on .zelari/plan.json, ADR-0018). Default: enabled for the full
   * profile and in planMode (the plan is the plan-phase domain). Never
   * readOnly/explore/verify/general.
   */
  enablePlanTasks?: boolean;
  /**
   * Optional sink for first-class task events (task_update / task_snapshot,
   * ADR-0018 3b) emitted by the plan-task tools after durable writes. The
   * headless runner wires it to the NDJSON stream so the Desktop Live Tasks
   * panel needs no tool parsing.
   */
  onTaskEvent?: PlanTaskEventSink;
  /**
   * v0.10.0 lifecycle hooks. Default: auto — global hooks always +
   * project hooks when the folder is trusted (parent profiles only).
   * Pass a runner to override; pass null to disable.
   */
  lifecycleHooks?: LifecycleHookRunner | null;
  /**
   * t30 (Pilastro C): pre-loaded extension runtime (src/cli/extensions/loader.ts
   * loads it ASYNC from disk — global dir always, project dir only when the
   * folder is trusted). Extension tools are registered through the SAME
   * wrapWithPermissions path as builtin tools (they can never widen the
   * parent policy; ContractCompiler still intersects LAST) and the collected
   * onPreToolUse handlers ride the t22 failure semantics. Null/undefined ⇒
   * no extensions: the disk load happens in the host, never in this sync
   * registry builder.
   */
  extensions?: ExtensionRegistry | null;
  /** Native project memory shared by task-tool tentacles. */
  memoryService?: MemoryService;
  memoryAutoWrite?: boolean;
}

/**
 * Create a fresh ToolRegistry pre-populated with the 5 built-in tools,
 * each wrapped with the safety layer (Task A2).
 *
 * Safety policy applied:
 *  - filesystem tools: resolveSandboxedPath() on every path arg; throws
 *    SandboxViolationError if the path escapes the root.
 *  - bash: assertShellAllowed() on the command; throws ShellBlockedError
 *    on any blocklist match. Since v2.17 (t27) the cwd arg is ALSO jailed:
 *    resolveSandboxedPath() resolves it against the root before the spawn
 *    and a cwd outside the root is a typed `[sandbox]` deny.
 *  - every tool: AuditLogger.runTool() wraps the call to record ts,
 *    args (redacted), ok, duration, summary.
 */
export function createBuiltinToolRegistry(
  options: CreateRegistryOptions = {},
): { registry: ToolRegistry; tools: BuiltinToolSummary[] } {
  const root = options.root ?? process.cwd();
  const audit = options.audit ?? new AuditLogger();
  const sessionId = options.sessionId ?? 'cli';

  // Wrap filesystem tools: sandbox the path argument before delegating.
  // Write tools (write_file/edit) are ALSO wrapped with the AST gate
  // (auto-revert on syntax-broken writes) and the diagnostics loop: after a
  // successful edit, a fast file-scoped checker runs on the touched file and
  // its errors/warnings are appended to the tool result so the model sees
  // compiler feedback in the same turn (opt out: ZELARI_DIAGNOSTICS=0).
  // t31 (2.21 §6.6): the execute tools (bash/exec_process) share the same
  // loop post-execute, on the source paths the invocation claims.
  // ADR-0033 (t77): edit_file/apply_diff left the default catalog — the
  // anchored `edit` tool is the single write surface besides write_file.
  const diagnosticsOn = options.diagnostics ?? process.env.ZELARI_DIAGNOSTICS !== '0';
  const withDiag = <I extends Record<string, unknown>, O>(t: ToolDefinition<I, O>) =>
    diagnosticsOn ? wrapWithDiagnostics(t, root, options.diagnosticsRunner) : t;
  // t31 (2.21 §6.6): the EXECUTE tools (bash/exec_process) get the same
  // diagnostics loop, post-execute, on the source paths the invocation
  // claims — same engine, same result channel, same opt-out as withDiag.
  const withExecDiag = <I extends Record<string, unknown>, O>(t: ToolDefinition<I, O>) =>
    diagnosticsOn ? wrapWithExecuteDiagnostics(t, root, options.diagnosticsRunner) : t;
  // Cache sits inside the sandbox wrap so permission + path checks still
  // run on every call; only the disk/search work is skipped on a hit.
  const safeReadFile = wrapWithSandbox(
    withResultCache(readFileTool, { kind: 'stat' }),
    ['path'],
    root,
    audit,
    sessionId,
  );
  // v2.16 (t26): write tools additionally re-verify containment (fresh
  // syscalls) immediately before execute — see wrapWithSandbox opts.verifyWrite.
  // t76: the AST gate sits between sandbox (abs-path rewrite) and diagnostics
  // (a reverted write surfaces as an error result, so withDiag skips it).
  const safeWriteFile = withDiag(
    wrapWithAstGate(
      wrapWithSandbox(writeFileTool, ['path'], root, audit, sessionId, { verifyWrite: true }),
      { root },
    ),
  );
  const safeEdit = withDiag(
    wrapWithAstGate(
      wrapWithSandbox(editTool, ['path'], root, audit, sessionId, { verifyWrite: true }),
      { root },
    ),
  );
  const safeGrepContent = wrapWithSandbox(
    withResultCache(grepContentTool, { kind: 'ttl' }),
    ['path'],
    root,
    audit,
    sessionId,
  );
  const safeListFiles = wrapWithSandbox(
    withResultCache(listFilesTool, { kind: 'ttl' }),
    ['path'],
    root,
    audit,
    sessionId,
  );
  const safeShowDiff = wrapWithSandbox(showDiffTool, ['path'], root, audit, sessionId);

  // v2.17 (t28, Pilastro A): ONE JailSpec per registry, from the SAME root
  // decision the sandbox wrappers use (no second policy engine). The bash
  // tool is rebuilt with the osJail spawn seam so its ONLY spawn route is
  // safety/osJail.spawnJailed; the network posture is narrowed per
  // invocation from the SAME layered claims engine the permission wrapper
  // uses (agentPolicyLayers below — the resolver only runs inside
  // execute(), never at construction, so the declaration order is safe).
  const jailSpec = buildJailSpec({ root });
  const resolveJailNetwork = (toolName: string, args: Record<string, unknown>): JailNetwork => {
    const allowedHosts: string[] = [];
    for (const claim of resourceClaimsFor(toolName, args)) {
      if (claim.kind !== 'network') continue;
      const hit = matchResourceClaimLayered(
        agentPolicyLayers,
        agentPolicySet.precedence,
        claim,
        root,
      );
      if (hit?.effect === 'allow') allowedHosts.push(claim.host);
    }
    return networkSpecFromClaimHosts(allowedHosts);
  };
  const jailedBashSeam: BashSpawnSeam = (req) => {
    const res = spawnJailed(jailSpec, req);
    if (res.outcome === 'spawned') {
      return { outcome: 'spawned', child: res.child, ...(res.notice ? { notice: res.notice } : {}) };
    }
    return res.outcome === 'denied'
      ? { outcome: 'denied', reason: res.reason }
      : { outcome: 'failed', reason: res.reason };
  };

  // Wrap bash: shell blocklist + sandboxed cwd (v2.17 t27) + OS-jail
  // preflight (v2.17 t28) + audit — spawned ONLY through the seam above.
  // t31 (2.21 §6.6): after a successful run, diagnostics run on the source
  // paths the command claims (post-execute, same loop as the edit tools).
  const safeBash = withExecDiag(
    wrapWithShellSafety(createBashTool(jailedBashSeam), audit, sessionId, root, jailSpec),
  );

  // P0.C2 (t17): structured exec_process — same shell-safety discipline as
  // bash (blocklist + audit) with an evidence-grade audit summary
  // (`program argv -> exitCode=N`); registered THROUGH the permission
  // wrapper below so policy claims gate every execution. v2.17 (t28): the
  // tool spawns via spawnJailed internally (network = claims verdict).
  // t31 (2.21 §6.6): post-execute diagnostics on claimed source paths.
  const safeExecProcess = withExecDiag(
    wrapWithExecEvidence(
      createExecProcessTool(root, {
        resolveNetwork: (input) => resolveJailNetwork('exec_process', input as unknown as Record<string, unknown>),
      }),
      audit,
      sessionId,
    ),
  );

  // v0.7.5: network tools — audit-only wrap (no filesystem paths to sandbox;
  // the tools themselves enforce http(s)-only + timeout + size caps).
  const safeFetchUrl = wrapWithAudit(fetchUrlTool, audit, sessionId);
  const safeWebSearch = wrapWithAudit(webSearchTool, audit, sessionId);

  const registry = new ToolRegistry();
  const profile = options.profile ?? 'full';

  // v0.10.0 P0: lifecycle hooks on parent registries. Sub-agent registries
  // (explore/verify/general) skip hooks — the parent already gates them.
  const isParent = !options.profile || options.profile === 'full';
  const hooks = isParent ? (options.lifecycleHooks ?? createDefaultLifecycleHooks(root)) : null;
  if (hooks) registry.setLifecycleHooks(hooks);
  // Read-only / plan / explore: observe only.
  // verify: observe + bash.
  // general: mutators but no nested task (handled below).
  const readOnly =
    options.readOnly === true ||
    options.planMode === true ||
    profile === 'explore';
  const verifyMode = profile === 'verify';
  const gauntletParent = options.gauntletParent === true;
  const allowMutators = !readOnly && !verifyMode && !gauntletParent;
  const allowBash = (allowMutators || verifyMode) && !gauntletParent;

  // t58 declared-vs-observed guard: on parent registries with mutators, flag
  // completed tasks whose declared files are written by a LATER session
  // (radio 'task_reopened' + plan.json flag, total fail-open — taskTouchGuard).
  if (isParent && allowMutators && sessionId) {
    registry.setToolResultListener(createTaskTouchGuard({ projectRoot: root, sessionId }));
    // t59 session-start staleness sweep: commits landed after completedAt on
    // declared files of old-enough completed tasks → flag 'stale' + radio
    // 'task_stale'. Fire-and-forget, advisory-only (ADR 0023).
    void runTaskStalenessCheck({ projectRoot: root, sessionId });
  }

  const permPolicy = options.permissionPolicy ?? defaultPermissionPolicy();
// P0.A layered policy engine: per-command/per-path rules for THIS agent
// identity with global and project kept as DISTINCT layers. Default
// evaluation is restrict-only (a global deny/ask can never be relaxed by the
// project file); ZELARI_POLICY_PRECEDENCE=legacy restores the v1
// project-first override. Loaded once per registry build; empty when
// absent/invalid/disabled (ZELARI_POLICY=0).
const agentPolicySet = (() => {
  // P0.B: load in the ACTIVE policy-load mode (resolvePolicyLoadMode:
  // ZELARI_POLICY_LOAD_MODE > strict for headless/CI/mission > permissive
  // TUI). Registry construction stays infallible — a strict-mode
  // PolicyLoadError degrades to the empty set with a loud stderr line here;
  // the HARD block happens in the runHeadless pre-flight gate
  // (headless/policyGate.ts), which returns exit 2 / `policy-load-failed`
  // before any registry is ever built.
  try {
    return loadPolicySet(root, { mode: activePolicyLoadMode() });
  } catch (err) {
    if (err instanceof PolicyLoadError) {
      process.stderr.write(`[policy] ${err.message} — registry continues WITHOUT agent rules.\n`);
      return emptyPolicySet();
    }
    throw err;
  }
})();
const agentPolicyLayers: LayeredPolicyRuleSet = agentLayersFor(
  agentPolicySet,
  options.policyAgent ?? 'lead',
);
  const withPerm = <I, O>(t: ToolDefinition<I, O>) =>
    wrapWithPermissions(
      t,
      permPolicy,
      options.onPermissionAsk,
      agentPolicyLayers,
      agentPolicySet.precedence,
      root,
    );

  // Observe tools — always registered.
  registry.register(withPerm(safeReadFile));
  registry.register(withPerm(safeGrepContent));
  registry.register(withPerm(safeListFiles));
  registry.register(withPerm(safeShowDiff));
  registry.register(withPerm(safeFetchUrl));
  registry.register(withPerm(safeWebSearch));

  // observe_batch — N read-only observations in one round-trip (2026-07
  // context-growth plan, Fase 1). Registered in BOTH full and read-only
  // registries; reuses the sandbox+permission+cache-wrapped tools above.
  // Kill switch ZELARI_OBSERVE_BATCH=0 (A/B against the Fase M baseline).
  const observeBatchTool =
    process.env.ZELARI_OBSERVE_BATCH !== '0'
      ? withPerm(
          createObserveBatchTool({
            tools: {
              read_file: safeReadFile,
              grep_content: safeGrepContent,
              list_files: safeListFiles,
            },
          }),
        )
      : null;
  if (observeBatchTool) {
    registry.register(observeBatchTool);
  }

  // retrieve_observation — rematerialize a projected tool result by seq
  // (2026-07 context-growth plan, Fase 2). Read-only; both registries.
  const retrieveObservationTool =
    process.env.ZELARI_SESSION_SURFACE !== '0'
      ? withPerm(createRetrieveObservationTool())
      : null;
  if (retrieveObservationTool) {
    registry.register(retrieveObservationTool);
  }

  // Mutating tools — full/general only.
  if (allowMutators) {
    registry.register(withPerm(safeWriteFile));
    registry.register(withPerm(safeEdit));
  }
  if (allowBash) {
    registry.register(withPerm(safeBash));
    // P0.C2: unlike legacy bash, exec_process is ALWAYS behind the
    // permission + resource-claims choke-point.
    registry.register(withPerm(safeExecProcess));
  }

  // Interactive clarification — available in plan + build (not pure explore RO).
  const askUserTool =
    options.readOnly === true ||
    profile === 'explore' ||
    profile === 'verify' ||
    gauntletParent
      ? null
      : createAskUserTool(options.onAskUser);
  if (askUserTool) {
    registry.register(withPerm(askUserTool));
  }

  // Lazy skill loader — parent (plan/build) and general subagent; not explore/verify.
  const enableSkill =
    options.enableSkill !== false &&
    options.readOnly !== true &&
    !gauntletParent &&
    profile !== 'explore' &&
    profile !== 'verify';
  const skillTool = enableSkill
    ? withPerm(createSkillTool({ cwd: root, pressureBand: options.pressureBand }))
    : null;
  if (skillTool) {
    registry.register(skillTool);
  }

  // Session todos — parent agent only (not explore/verify; general skips like OpenCode).
  const enableTodos =
    options.enableTodos !== false &&
    options.readOnly !== true &&
    !gauntletParent &&
    profile === 'full';
  const todoWrite = enableTodos ? withPerm(createTodoWriteTool()) : null;
  const todoRead = enableTodos ? withPerm(createTodoReadTool()) : null;
  if (todoWrite) registry.register(todoWrite);
  if (todoRead) registry.register(todoRead);

  // Durable workspace plan tasks (ADR-0018) - .zelari/plan.json store shared
  // with the council stubs. Full build agent + plan phase only.
  const enablePlanTasks =
    options.enablePlanTasks !== false &&
    options.readOnly !== true &&
    !gauntletParent &&
    (profile === 'full' || options.planMode === true) &&
    profile !== 'explore' &&
    profile !== 'verify' &&
    profile !== 'general';
  const planTaskToolsWrapped = (
    enablePlanTasks
      ? createPlanTaskTools({
          projectRoot: root,
          onTaskEvent: options.onTaskEvent,
          sessionId,
        })
      : []
  ).map((t) => withPerm(t));
  for (const t of planTaskToolsWrapped) {
    registry.register(t);
  }

  const summary = [
    safeReadFile,
    safeGrepContent,
    safeListFiles,
    safeShowDiff,
    safeFetchUrl,
    safeWebSearch,
    ...(observeBatchTool ? [observeBatchTool] : []),
    ...(retrieveObservationTool ? [retrieveObservationTool] : []),
    ...(allowMutators ? [safeWriteFile, safeEdit] : []),
    ...(allowBash ? [safeBash, safeExecProcess] : []),
    ...(askUserTool ? [askUserTool] : []),
    ...(skillTool ? [skillTool] : []),
    ...(todoWrite ? [todoWrite] : []),
    ...(todoRead ? [todoRead] : []),
    ...(planTaskToolsWrapped.length > 0 ? planTaskToolsWrapped : []),
  ];
  const tools: BuiltinToolSummary[] = summary.map((t) => ({
    name: t.name,
    description: t.description,
    permissions: t.permissions ?? [],
  }));

  // inspect_command — allowlisted no-shell inspector (git status/log/diff/
  // show, tsc --noEmit with S3.5 artifact safety) registered exactly where
  // bash is NOT: readOnly subagents, planMode sessions and the explore
  // profile. Full/verify keep the real bash. Kill-switch: ZELARI_INSPECT_COMMAND=0.
  if (readOnly && process.env.ZELARI_INSPECT_COMMAND !== '0') {
    const inspectTool = createInspectCommandTool(root);
    registry.register(withPerm(inspectTool));
    tools.push({
      name: inspectTool.name,
      description: inspectTool.description,
      permissions: inspectTool.permissions ?? [],
    });
  }

  // AST structural tools (ast_outline, find_symbol) — read-only, so available
  // in BOTH the full registry and read-only sub-agents. Gated by ZELARI_AST.
  if (process.env.ZELARI_AST !== '0') {
    for (const t of createAstTools(root)) {
      registry.register(t);
      tools.push({ name: t.name, description: t.description, permissions: t.permissions ?? [] });
    }
  }

  // Semantic code search — read-only, available in both registries. Gated by
  // ZELARI_SEMANTIC. Needs a prior index (/index); the tool self-reports when
  // none exists, so it's always safe to register.
  if (process.env.ZELARI_SEMANTIC !== '0') {
    const semanticTool = createSemanticTool({ root });
    registry.register(semanticTool);
    tools.push({
      name: semanticTool.name,
      description: semanticTool.description,
      permissions: semanticTool.permissions ?? [],
    });
  }

  // Browser verification (browser_check) — full registry only (it drives a
  // real browser). Gated by ZELARI_BROWSER; self-reports install steps when
  // Playwright is absent, so it's safe to register unconditionally.
  if (!readOnly && !gauntletParent && process.env.ZELARI_BROWSER !== '0') {
    const browserTool = createBrowserTool();
    registry.register(browserTool);
    tools.push({
      name: browserTool.name,
      description: browserTool.description,
      permissions: browserTool.permissions ?? [],
    });
  }

  // Screen capture (screenshot) — full registry only; 'ui' permission keeps
  // the user in control of every capture. Gated by ZELARI_SCREENSHOT=0.
  if (!readOnly && !gauntletParent && process.env.ZELARI_SCREENSHOT !== '0') {
    const screenshotTool = createScreenshotTool();
    registry.register(screenshotTool);
    tools.push({
      name: screenshotTool.name,
      description: screenshotTool.description,
      permissions: screenshotTool.permissions ?? [],
    });
  }

  // SSH deploy/monitor tools — full registry only. Gated by ZELARI_SSH=0.
  // Targets from ~/.zelari-code/ssh-targets.json; ssh_run is allowlist-only.
  if (!readOnly && !gauntletParent && process.env.ZELARI_SSH !== '0') {
    for (const t of createSshTools()) {
      registry.register(t);
      tools.push({
        name: t.name,
        description: t.description,
        permissions: t.permissions ?? [],
      });
    }
  }

  // Schema-inspired world model (hypothesis / checks / run_backtest / timeline).
  // Full registry only. Kill switch: ZELARI_SCHEMA_LOOP=0.
  if (!readOnly && !gauntletParent) {
    for (const t of createWorldModelTools()) {
      const safe = wrapWithAudit(t as ToolDefinition<Record<string, unknown>, unknown>, audit, sessionId);
      registry.register(safe);
      tools.push({
        name: t.name,
        description: t.description,
        permissions: t.permissions ?? [],
      });
    }
  }

  // The `task` sub-agent tool — parent full registry only (not explore/verify/general
  // sub-agents). Sub-agents get a profile-specific registry and never nest another
  // `task`. Fase 1 (ADR-0020): PLAN keeps the `task` tool restricted to
  // explore-only (plan-safe) tentacles unless planExploreTask=false; the explore
  // profile is read-only, so PLAN gains parallel research without write/execute reach.
  const enableTask =
    options.enableTask !== false &&
    options.readOnly !== true &&
    !verifyMode &&
    profile === 'full' &&
    (!options.planMode || options.planExploreTask !== false);
  if (enableTask) {
    const taskTool = createTaskTool(
      {
        createSubAgentContext: createKrakenSubAgentContextFactory({
          root,
          audit,
          sessionId,
          // P0.4 capability inheritance: tentacles intersect THIS
          // registry's own policy (permPolicy above) — they can never
          // exceed it.
          parentPolicy: permPolicy,
          ...(options.onPermissionAsk ? { onPermissionAsk: options.onPermissionAsk } : {}),
          ...(options.subAgentProvider ? { provider: options.subAgentProvider } : {}),
          ...(options.subAgentModel ? { model: options.subAgentModel } : {}),
        }),
        ...(options.memoryService ? { memoryService: options.memoryService } : {}),
        ...(options.memoryAutoWrite !== undefined
          ? { memoryAutoWrite: options.memoryAutoWrite }
          : {}),
        ...(options.onTentacleEvent ? { onTentacleEvent: options.onTentacleEvent } : {}),
      },
      options.planMode === true ? { allowedAgents: ['explore'] } : undefined,
    );
    registry.register(withPerm(taskTool));
    tools.push({
      name: taskTool.name,
      description: taskTool.description,
      permissions: taskTool.permissions ?? [],
    });
  }

  // Fase 4 (ADR-0020): `kraken_select` — parent Kraken ONLY. Gated on
  // enableTask (full profile, not read-only, not verifyMode) AND the
  // explicit krakenSelect option, so tentacles/council/zelari never see
  // it. The verifier defaults to the EXACT parent provider/model
  // (subAgentProvider/subAgentModel of this turn/run).
  if (options.krakenSelect === true && enableTask) {
    const selectTool = createKrakenSelectTool({
      loadParentIdentity: async () => {
        const cfg = options.subAgentProvider
          ? await providerConfigFor(options.subAgentProvider as ProviderName)
          : await providerFromEnv();
        if (!cfg) return null;
        return {
          provider: cfg.providerId,
          model: options.subAgentModel || cfg.model,
        };
      },
      loadStream: async (provider) => {
        const cfg = await providerConfigFor(provider as ProviderName);
        return cfg ? buildProviderStream(cfg) : null;
      },
      // Fase 9 (ADR-0020): persisted verifier override (provider.json
      // `krakenVerifier`). undefined = inherit the EXACT parent model.
      loadVerifierOverride: () => getKrakenVerifierOverride(),
    });
    registry.register(withPerm(selectTool));
    tools.push({
      name: selectTool.name,
      description: selectTool.description,
      permissions: selectTool.permissions ?? [],
    });
  }

  // LSP navigation tools (go_to_definition, find_references, hover_type,
  // document_symbols, rename_symbol). Full + plan (read-only) registry,
  // gated by ZELARI_LSP. All five tools are permissions: ['read']
  // (rename_symbol is preview-only), so plan sessions keep the full
  // navigation ladder ast → lsp → grep → read_file.
  // The shared manager degrades into an explicit degraded field in tool
  // results when a server is not installed (EMPTY is never silently faked).
  if (process.env.ZELARI_LSP !== '0' && options.lspProvider !== null) {
    const lspTools = options.lspProvider
      ? createLspTools(options.lspProvider, root)
      : createLspTools(getSharedLspManager(root), root);
    for (const t of lspTools) {
      registry.register(t);
      tools.push({ name: t.name, description: t.description, permissions: t.permissions ?? [] });
    }
  }

  // t30 (Pilastro C): ExtensionAPI seam. Extension tools enter through the
  // SAME `withPerm` wrapper as every builtin above — category policy, agent
  // rules, resource claims and the TaskContract capability layer all still
  // intersect (contract LAST), so a declared permission can never widen the
  // parent policy. Tool names never collide: an already-registered tool
  // wins and the extension tool is skipped LOUDLY (no shadowing builtins).
  const extRegistry = options.extensions;
  if (extRegistry) {
    for (const entry of extRegistry.listExtensionTools()) {
      if (registry.get(entry.spec.name)) {
        process.stderr.write(
          `[extensions] tool "${entry.spec.name}" from extension "${entry.extensionId}" collides with an existing tool — skipped\n`,
        );
        continue;
      }
      registry.register(withPerm(extensionToolToDefinition(entry)));
    }
    // Extension onPreToolUse handlers: additive post-pass over EVERY tool
    // (builtins included), mirroring the PreToolUse phase ordering (hooks
    // run before the tool body). t22 failure semantics via the SAME
    // resolver that drives the JSON lifecycle hooks. No handlers ⇒ no wrap,
    // zero overhead on registries without extensions.
    const preHandlers = extRegistry.preToolUseHandlers;
    if (preHandlers.length > 0) {
      const failureMode = resolveHookFailureMode(process.env[HOOKS_FAILURE_ENV]);
      const wrapExtPre = withExtensionPreToolUse(preHandlers, { failureMode });
      for (const name of registry.list()) {
        const def = registry.get(name);
        if (def) registry.register(wrapExtPre(def));
      }
    }
  }

  return { registry, tools };
}

/**
 * The 10 harness builtin tool names already bridged into the agents catalog
 * by harnessToolBridge.ts (getHarnessToolDefinitions). Used by
 * {@link getCliToolCatalogEntries} to skip them — re-bridging would duplicate
 * catalog entries (the dedupe-by-name in buildBuiltinRegistry would drop the
 * duplicate anyway, but skipping is cleaner and avoids warn noise).
 */
const HARNESS_BUILTIN_NAMES: ReadonlySet<string> = new Set([
  'read_file',
  'write_file',
  'edit',
  // ADR-0033 t77: edit_file/apply_diff left the default catalog, but the
  // harness bridge still exposes them for the deprecation cycle — keep
  // skipping them here so catalog re-bridging never duplicates.
  'edit_file',
  'bash',
  'grep_content',
  'list_files',
  'show_diff',
  'apply_diff',
  'fetch_url',
  'web_search',
]);

/**
 * Derive agents-catalog entries for the CLI-only tools in an executor registry
 * (browser_check, the LSP navigation tools, AST outline, semantic search).
 *
 * The council and zelari paths advertise tools through the static catalog
 * (`getAllTools()` → `getProviderTools()`), not through the executor's
 * `toOpenAITools()` like the main agent does. browser_check / LSP / AST are
 * registered in the executor (so `filterExecutable` keeps their names) but
 * absent from the catalog, so `getProviderTools` silently dropped them — the
 * council's models were never told these tools existed.
 *
 * This bridges that gap: for every tool in `registry` that is NOT one of the
 * 10 harness builtins, derive an `EnhancedToolDefinition` (same JSON Schema
 * the executor uses, guard-stub `execute` since real execution flows through
 * the council's shared ToolRegistry). The CLI then registers these into the
 * catalog via {@link registerCliToolsIntoCouncilCatalog}.
 *
 * Kill-switches are respected at registration time (createBuiltinToolRegistry
 * only registers browser_check when ZELARI_BROWSER !== '0', etc.), so this
 * function simply reflects whatever the executor actually has — no env check
 * needed here.
 *
 * @param registry  The executor registry (typically the council's shared one).
 * @returns Catalog entries for every non-harness-builtin tool present.
 */
export function getCliToolCatalogEntries(registry: ToolRegistry): EnhancedToolDefinition[] {
  const entries: EnhancedToolDefinition[] = [];
  for (const name of registry.list()) {
    if (HARNESS_BUILTIN_NAMES.has(name)) continue;
    const def = registry.get(name);
    if (!def) continue;
    try {
      entries.push(cliToolToEnhanced(def as unknown as ToolDefinition<never, unknown>));
    } catch {
      // A tool whose inputSchema can't be converted to JSON Schema is skipped
      // (matches getProviderTools' own skip-on-no-schema behaviour). Never throw.
    }
  }
  return entries;
}

/**
 * Register the CLI-only tools (browser_check, LSP, AST, semantic) into the
 * shared agents catalog so the council and zelari paths advertise them.
 *
 * Companion to the workspace-stub injection in councilDispatcher.ts: same
 * mechanism (registerCustomTool), same direction (CLI → core), same contract
 * (catalog entries carry schemas; execution flows through the executor).
 *
 * Idempotent in effect: registerCustomTool replaces by name, so re-calling
 * with the same registry is safe. Safe to call with a registry that has only
 * harness builtins (returns without registering anything).
 */
export function registerCliToolsIntoCouncilCatalog(registry: ToolRegistry): void {
  for (const entry of getCliToolCatalogEntries(registry)) {
    try {
      registerCustomTool(entry);
    } catch {
      // registerCustomTool rebuilds an internal index; a failure there must
      // never block the council from booting. Swallow and continue.
    }
  }
}


function taskAgentToProfile(agent: TaskAgentKind): 'explore' | 'verify' | 'general' {
  if (agent === 'general') return 'general';
  if (agent === 'verify') return 'verify';
  return 'explore';
}

/**
 * Build the `TaskToolDeps.createSubAgentContext` closure used by the `task`
 * tool: resolves a cheap/strong sub-model, builds a profile-scoped
 * registry (no nested `task`), and wraps it into a `SubAgentContext`.
 * Exported so other Kraken orchestration entry points — the graph executor
 * (F3) driven from the `/kraken graph` slash handler and headless flag
 * (F6) — can build a `TaskToolDeps` without duplicating this wiring.
 */
export function createKrakenSubAgentContextFactory(opts: {
  root: string;
  audit: AuditLogger;
  sessionId: string;
  /**
   * Explicit provider/model to anchor sub-agents to (e.g. the resolved
   * --provider/--model of a `--kraken-graph` headless run, itself sourced
   * from Desktop's provider/model selector). Without this, every tentacle
   * silently used the persisted `provider.json` `activeProviderId` instead
   * of whatever the caller actually selected — for the graph executor,
   * where ~all real work happens in tentacles, that made the provider
   * picker effectively a no-op. Falls back to `providerFromEnv()`'s
   * persisted default when omitted, preserving the `task` tool's existing
   * behavior exactly (it doesn't pass an override today).
   */
  provider?: string;
  model?: string;
  /**
   * P0.4 capability inheritance: the PARENT agent's permission policy.
   * The sub-agent registry is built with intersectPermissionPolicy(
   * parentPolicy, subProfilePolicy) — deny > ask > allow per category — so
   * a tentacle can NEVER hold more permission than its parent. Callers
   * that have a policy in scope (the parent registry builder, headless
   * runs) pass it here; when omitted the all-allow/auto default is used,
   * which makes the intersection a no-op rather than an escalation.
   */
  parentPolicy?: PermissionPolicy;
  /**
   * Parent ask-bridge. Tentacles used to omit this, so an intersected
   * `ask` (execute/network under standard) fail-closed in ~0s after the
   * user had already approved the parent `task` spawn.
   */
  onPermissionAsk?: PermissionAskHandler;
}): TaskToolDeps['createSubAgentContext'] {
  const { root, audit, sessionId, provider: providerOverride, model: modelOverride, parentPolicy, onPermissionAsk } = opts;
  return async ({ agent, cwd: subCwd }) => {
    const cfg = providerOverride
      ? await providerConfigFor(providerOverride as ProviderName)
      : await providerFromEnv();
    if (!cfg) return null;
    const { resolveKrakenSubModelAsync, parseQualifiedModelRef } = await import('./tools/krakenModel.js');
    const parentModel = modelOverride || cfg.model;
    // Async resolve (this factory is already async): forwards the ACTIVE
    // provider's discovered models, so the two routing options that used to be
    // dead code actually engage — cheap auto-pick for explore/verify and the
    // cross-family verify pick (P0.6). Without a models registry both lists
    // stay empty and this resolves the parent model exactly as before.
    const resolvedModel = await resolveKrakenSubModelAsync(agent, parentModel, process.env, {
      provider: cfg.providerId,
    });
    // Cross-provider tentacles (Desktop Settings → ZELARI_KRAKEN_*_MODEL): a
    // provider-qualified ref ("grok/grok-4") selects both provider and model.
    // Unknown provider → keep the raw id (previous behavior: sent to the lead
    // provider unchanged).
    let effCfg = cfg;
    let model = resolvedModel;
    const ref = parseQualifiedModelRef(resolvedModel);
    if (ref) {
      const cross =
        ref.provider === cfg.providerId
          ? cfg
          : await providerConfigFor(ref.provider as ProviderName);
      if (cross) {
        effCfg = cross;
        model = ref.model;
      }
    }
    const subCfg = { ...effCfg, model };
    const subProfile = taskAgentToProfile(agent);
    const subRoot = subCwd || root;
    // P0.4 capability inheritance: a tentacle NEVER holds more permission
    // than its parent. The sub-profile policy this factory builds is
    // headless-style auto-allow (a tentacle has no interactive prompt to
    // ask at); the effective policy is its intersection with the parent
    // policy — deny > ask > allow per category, `auto` is the AND of both.
    // If the intersection downgrades a category to `ask`, the tentacle
    // reuses the parent's onPermissionAsk (Desktop in-chat card / TUI
    // picker). Session grants from "Always this category" apply process-
    // wide, so a granted spawn does not re-ask inside the tentacle.
    // Without a handler, wrapWithPermissions still fail-closes (typedErr).
    const agentPolicyForSubProfile = defaultPermissionPolicy({ auto: true });
    const effectiveSubPolicy = intersectPermissionPolicy(
      parentPolicy ?? agentPolicyForSubProfile,
      agentPolicyForSubProfile,
    );
    const { registry: subRegistry } = createBuiltinToolRegistry({
      root: subRoot,
      audit,
      sessionId,
      profile: subProfile,
      enableTask: false,
      enableSkill: agent === 'general',
      diagnostics: false,
      lspProvider: null,
      permissionPolicy: effectiveSubPolicy,
      ...(onPermissionAsk ? { onPermissionAsk } : {}),
      // P0.5: the tentacle's agent identity drives per-agent policy rules.
      policyAgent: agent,
    });
    const wrapTentacleStream = (
      primary: ReturnType<typeof buildProviderStream>,
      primaryProviderId: string,
    ) =>
      wrapWithHeadlessFailover({
        primary,
        primaryProviderId,
        validProviderIds: PROVIDERS.map((p) => p.id),
        lookupFallbackConfig: (id) => providerConfigFor(id as ProviderName),
        buildStream: (config) =>
          buildProviderStream(config as Parameters<typeof buildProviderStream>[0]),
      });
    const providerStream = await wrapTentacleStream(
      buildProviderStream(subCfg),
      subCfg.providerId,
    );
    return {
      providerStream,
      model,
      provider: subCfg.providerId,
      ...(model !== parentModel
        ? {
            fallback: {
              model: parentModel,
              provider: cfg.providerId,
              // Wrap the 404-parent fallback too so a later model retry still
              // has transport failover. Orthogonal to isUnknownModelError.
              providerStream: await wrapTentacleStream(
                buildProviderStream({ ...cfg, model: parentModel }),
                cfg.providerId,
              ),
            },
          }
        : {}),
      registry: subRegistry,
      tools: subRegistry.toOpenAITools().map((t) => ({
        name: t.function.name,
        description: t.function.description,
        parameters: t.function.parameters,
      })),
      agent,
      cwd: subRoot,
    };
  };
}

/**
 * Gate tool execution with allow/ask/deny policy (OpenCode-style permissions).
 */
function wrapWithPermissions<I, O>(
  original: ToolDefinition<I, O>,
  policy: PermissionPolicy,
  onAsk?: PermissionAskHandler,
  agentLayers?: LayeredPolicyRuleSet,
  precedence: PolicyPrecedence = 'restrict-only',
  root?: string,
): ToolDefinition<I, O> {
  const required = (original.permissions ?? []) as ToolPermission[];
  // Pure read tools under allow policy — no wrap overhead.
  const decisionProbe = resolveToolPermission(original.name, required, policy);
  if (decisionProbe.action === 'allow' && !required.includes('write') && !required.includes('execute')) {
    // Still wrap write/execute categories; for read-only allow skip? Keep wrap
    // always for consistency when policy might deny network later.
  }
  return {
    ...original,
    execute: async (input: I, ctx: ToolContext): Promise<TypedResult<O>> => {
      const requiredNow: readonly ToolPermission[] =
        original.name === 'task'
          ? permissionsForTaskAgent(
              (input as { agent?: TaskAgentKind } | null | undefined)?.agent,
            )
          : required;
      const decision = resolveToolPermission(original.name, requiredNow, policy);
      // P0.A: resolve this agent's rule across the global+project layers —
      // restrict-only by default (deny > ask > allow; a global deny/ask can
      // never be relaxed by the project file; legacy precedence restores the
      // v1 project-first override). The survivor only ever ADDS restriction
      // to the category decision (mergeRuleEffect).
      const rule = agentLayers
        ? matchAgentPolicyRuleLayered(
            agentLayers,
            precedence,
            requiredNow,
            (input ?? {}) as Record<string, unknown>,
            root ?? process.cwd(),
          )
        : null;
      // P0.C1 resource claims: EVERY resource this invocation can touch gets
      // its own layered match and the per-claim effects intersect into the
      // decision (deny > ask > allow) — a multi-path tool can no longer pass
      // because only its first path was checked. No matched claim ⇒
      // undefined ⇒ the category + single-rule decision is untouched.
      const claims = agentLayers
        ? resolveClaimsVerdict(
            agentLayers,
            precedence,
            original.name,
            (input ?? {}) as Record<string, unknown>,
            root ?? process.cwd(),
          )
        : undefined;
      // t22: the active TaskContract's compiled capability layer matches as a
      // THIRD independent source and its effect intersects LAST — a contract
      // deny can never be relaxed and a contract allow can never widen any
      // other layer or the category decision (same deny>ask>allow lattice).
      // No scoped contract ⇒ null ⇒ this slot is inert.
      const contractRule = matchContractCapabilityRule(
        requiredNow,
        (input ?? {}) as Record<string, unknown>,
        root ?? process.cwd(),
      );
      let action = intersectEffects(mergeRuleEffect(decision.action, rule), claims?.effect, contractRule?.effect);
      // W3.1 (t46): provenance escalation at the choke-point. Write/execute
      // args that EMBED fingerprinted non-user content (web fetch, MCP output,
      // file reads — safety/provenance.ts) escalate an "allow" to "ask":
      // injected instructions can no longer sail through on category
      // defaults. Deterministic substring match, zero LLM (P2); ask/deny
      // already gated pass through; ZELARI_PROVENANCE=0 opts out entirely.
      let actionReason = decision.reason;
      if (action !== 'deny' && (requiredNow.includes('write') || requiredNow.includes('execute'))) {
        const provHit = provenanceMatchIn(JSON.stringify(input ?? {}));
        if (provHit && provenanceAppliesTo(provHit.source, requiredNow)) {
          const provNote = `[provenance] args embed non-user ${provHit.source} content (via ${provHit.tool})`;
          if (action === 'allow') {
            action = 'ask';
            actionReason = `${provNote} — confirm before ${requiredNow.join('+')}`;
          } else {
            actionReason = `${decision.reason} · ${provNote}`;
          }
        }
      }
      // v2.32 (S5): destructive-shape escalation at the SAME choke-point.
      // A command whose shape is inherently destructive (rm -rf, del /s,
      // format, git push --force, mkfs, dd-to-device — safety/
      // destructiveCommands.ts) escalates an "allow" to "ask" even with
      // execute=allow: outside-write discipline, exec side. yolo keeps its
      // contract (explicit opt-in stays allow); an in-session grant for this
      // tool (the user already answered THIS ask) is honored. Deterministic
      // regex, zero LLM (P2); ask/deny already gated pass through untouched.
      if (
        action === 'allow' &&
        requiredNow.includes('execute') &&
        activePermissionPreset() !== 'yolo' &&
        !isSessionGranted(original.name, requiredNow)
      ) {
        const destructiveHit = destructiveCommandHit((input ?? {}) as Record<string, unknown>);
        if (destructiveHit) {
          action = 'ask';
          actionReason = `[destructive] ${destructiveHit} - confirm before execute`;
        }
      }
      const claimHit =
        claims && claims.effect !== undefined
          ? claims.matchedRules.find((x) => x.effect === claims.effect)
          : undefined;
      const rulePrefix = contractRule
        ? `[contract] rule '${contractRule.match}'${contractRule.reason ? ` — ${contractRule.reason}` : ''}`
        : rule
          ? `[policy] rule '${rule.match}'${rule.reason ? ` — ${rule.reason}` : ''}`
          : claimHit
            ? `[policy] claim '${claimHit.match}'${claimHit.reason ? ` — ${claimHit.reason}` : ''}`
            : '';
      if (action === 'deny') {
        return typedErr(`[permission] ${rulePrefix || decision.reason}`);
      }
      if (action === 'ask') {
        if (!onAsk) {
          return typedErr(
            `[permission] ${rulePrefix ? `${rulePrefix} ` : ''}${decision.reason} No interactive approval available ` +
              `(set ZELARI_AUTO=1 to auto-allow, or configure onPermissionAsk).`,
          );
        }
        try {
          // v2.20: attach the policy engine's claim expansion (what this
          // approval unlocks) + the matched rule that forced the ask. With
          // no policy layers the expansion still comes from resourceClaimsFor
          // so the picker always shows concrete resources, never bare
          // category names.
          const expanded =
            claims?.claims ??
            resourceClaimsFor(original.name, (input ?? {}) as Record<string, unknown>);
          const ok = await onAsk({
            toolName: original.name,
            reason: actionReason || decision.reason,
            categories: decision.categories,
            args: input,
            policyNote: rulePrefix || undefined,
            claims: expanded.slice(0, 6).map((c) => ({
              kind: c.kind,
              summary: describeResourceClaim(c),
            })),
          });
          if (!ok) {
            return typedErr(
              `[permission] User denied "${original.name}" (${decision.categories.join(', ')}).`,
            );
          }
        } catch (err) {
          return typedErr(
            `[permission] Approval UI failed: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      }
      const outcome = await original.execute(input, ctx);
      // W3.1 (t46): fingerprint non-mutating tool results (web/mcp/file) so a
      // LATER write/execute embedding this content escalates to ask.
      recordResultForProvenance(original.name, requiredNow, outcome);
      return outcome;
    },
  };
}

/**
 * Wrap a filesystem tool so every path argument is sandboxed to `root` before
 * the tool runs: lexical + symlink-safe resolution (resolveSandboxedPath), a
 * typed SandboxViolationError → `{ ok: false, error: '[sandbox] …' }` deny,
 * and an audit record for every violation.
 *
 * v2.16 (t26): write tools (write_file / edit_file / apply_diff) pass
 * `{ verifyWrite: true }` — the resolved destination is re-checked with
 * verifyContainment (BOTH layers, fresh syscalls) as the LAST step before
 * execute, closing the TOCTOU window between the resolve above and the
 * actual disk mutation (links swapped during an approval UI / hook await).
 * Read-side tools stay resolve-only.
 */
export function wrapWithSandbox<I extends Record<string, unknown>, O>(
  original: ToolDefinition<I, O>,
  pathArgs: readonly string[],
  root: string,
  audit: AuditLogger,
  sessionId: string,
  opts: { verifyWrite?: boolean } = {},
): ToolDefinition<I, O> {
  return {
    ...original,
    execute: async (rawArgs: I, ctx: ToolContext): Promise<TypedResult<O>> => {
      // Pre-flight: sandbox all path args; rewrite them in-place.
      const args = rawArgs as Record<string, unknown>;
      // Audit + typedErr so the caller gets a friendly error (the resolver
      // below and the t26 re-check share the same deny shape).
      const denySandbox = async (err: SandboxViolationError): Promise<TypedResult<O>> => {
        await audit.append({
          ts: new Date().toISOString(),
          sessionId,
          tool: original.name,
          args: redactForAudit(args),
          ok: false,
          resultSummary: err.message,
          durationMs: 0,
          error: 'sandbox_violation',
        });
        return {
          ok: false,
          error: `[sandbox] ${err.message}`,
        } as TypedResult<O>;
      };
      for (const key of pathArgs) {
        const v = args[key];
        if (typeof v === 'string' && v.length > 0) {
          try {
            args[key] = resolveSandboxedPath(v, { root });
          } catch (err) {
            if (err instanceof SandboxViolationError) {
              return denySandbox(err);
            }
            throw err;
          }
        }
      }
      // v2.16 (t26): TOCTOU re-check on the write path — verifyContainment
      // re-runs BOTH layers on fresh syscalls as the last step before the
      // tool mutates disk. Same EPERM → lexical-layer degradation semantics
      // as sandboxPath itself (reused as-is, not reimplemented).
      if (opts.verifyWrite) {
        for (const key of pathArgs) {
          const v = args[key];
          if (typeof v !== 'string' || v.length === 0) continue;
          try {
            verifyContainment(v, { root });
          } catch (err) {
            if (err instanceof SandboxViolationError) {
              return await denySandbox(err);
            }
            throw err;
          }
        }
      }
      // Audit-wrapped execution.
      try {
        return await audit.runTool({
          tool: original.name,
          args: redactForAudit(args),
          sessionId,
          fn: () => original.execute(rawArgs, ctx),
        });
      } catch (err) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : String(err),
        } as TypedResult<O>;
      }
    },
  };
}

/**
 * Wrap an edit tool (write_file / edit_file / apply_diff) with the
 * post-edit diagnostics loop. After a successful, non-dry-run edit, a fast
 * file-scoped checker runs on the touched file (`result.value.path`) and, if
 * it finds anything, a compact diagnostics block is appended to the result
 * value under `diagnostics`. The harness serializes the value into the tool
 * message, so the model sees compiler errors in the same turn and can fix
 * them immediately.
 *
 * Best-effort and non-blocking-by-design: unsupported file types, missing
 * linters, timeouts, and parse failures all yield no diagnostics and leave
 * the original result untouched. Never changes a failed result or a dryRun.
 */
function wrapWithDiagnostics<I extends Record<string, unknown>, O>(
  original: ToolDefinition<I, O>,
  root: string,
  runner?: Runner,
): ToolDefinition<I, O> {
  return {
    ...original,
    execute: async (rawArgs: I, ctx: ToolContext): Promise<TypedResult<O>> => {
      const result = await original.execute(rawArgs, ctx);
      if (!result.ok) return result;
      // A dry-run edit (apply_diff dryRun) writes nothing — nothing to check.
      if ((rawArgs as Record<string, unknown>).dryRun === true) return result;
      const value = result.value as { path?: unknown } | null;
      const filePath =
        value && typeof value === 'object' && typeof value.path === 'string'
          ? value.path
          : undefined;
      if (!filePath) return result;
      try {
        const timeoutMs = Number(process.env.ZELARI_DIAGNOSTICS_TIMEOUT_MS) || 5000;
        const diags = await runDiagnosticsForFile(filePath, {
          cwd: root,
          timeoutMs,
          ...(runner ? { runner } : {}),
        });
        const formatted = formatDiagnostics(diags, { relativeTo: root });
        if (formatted) {
          return {
            ok: true,
            value: { ...(value as Record<string, unknown>), diagnostics: formatted },
          } as TypedResult<O>;
        }
      } catch {
        // Diagnostics must never break an edit — swallow and return as-is.
      }
      return result;
    },
  };
}

// ---------------------------------------------------------------------------
// t31 (2.21 §6.6): post-execute diagnostics on CLAIMED source paths for the
// execute tools (bash / exec_process). The edit tools run the loop through
// wrapWithDiagnostics; an execute touches source files too (`node gen.ts`,
// `eslint src/a.ts`) — after a successful run, the SAME fast file-scoped
// checker runs on the source paths the invocation claims (the process
// claims of resourceClaimsFor — the SAME claims table the permission and
// jail-network layers read) and the formatted block lands under
// `diagnostics` in the result value — the SAME channel, no second mechanism.
// Conservative by construction: a token counts only when it (a) has an
// extension the diagnostics engine can check (DEFAULT_PROVIDERS surface),
// (b) resolves INSIDE the workspace root (same sandbox policy as path args),
// and (c) exists on disk after the run. Missing/outside paths are skipped.
// Best-effort like the edit loop: never blocks, never fails the tool.
// ---------------------------------------------------------------------------
const DIAG_SOURCE_EXTENSIONS: ReadonlySet<string> = new Set(
  DEFAULT_PROVIDERS.flatMap((p) => p.extensions),
);
/** Cap the post-execute fan-out (an execute can mention many paths). */
const EXEC_DIAGNOSTICS_MAX_FILES = 4;

/** One claimed token → existing in-root source path, or null. */
function claimedSourcePath(
  token: string,
  args: Record<string, unknown>,
  root: string,
): string | null {
  const cleaned = token.replace(/^["']|["']$/g, '');
  if (!cleaned || cleaned.startsWith('-')) return null; // flags are not paths
  if (!DIAG_SOURCE_EXTENSIONS.has(path.extname(cleaned).toLowerCase())) return null;
  // Bases: the (jailed, absolute) cwd the child ran in, then the root — a
  // relative cwd value still resolves against the root, never process.cwd().
  const bases = [root];
  const cwd = args['cwd'];
  if (typeof cwd === 'string' && cwd.length > 0) {
    bases.unshift(path.isAbsolute(cwd) ? cwd : path.resolve(root, cwd));
  }
  for (const base of bases) {
    const candidate = path.isAbsolute(cleaned) ? path.normalize(cleaned) : path.resolve(base, cleaned);
    try {
      // Same containment policy as every path arg (symlink-safe resolve).
      const contained = resolveSandboxedPath(candidate, { root });
      if (existsSync(contained)) return contained;
    } catch {
      // Resolves outside the root → not a workspace source path; skip.
    }
  }
  return null;
}

/** Source paths claimed by an execute invocation (process claims → tokens). */
function claimedSourcePaths(
  toolName: string,
  args: Record<string, unknown>,
  root: string,
): string[] {
  const out: string[] = [];
  for (const claim of resourceClaimsFor(toolName, args)) {
    if (claim.kind !== 'process') continue;
    const tokens = [
      claim.executable,
      ...(claim.argv ?? []),
      ...(claim.raw ? claim.raw.split(/\s+/) : []),
    ];
    for (const token of tokens) {
      const p = claimedSourcePath(token, args, root);
      if (p && !out.includes(p)) out.push(p);
    }
  }
  return out;
}

/**
 * Wrap an execute tool (bash / exec_process) with the post-execute
 * diagnostics loop over its claimed source paths. After a successful run,
 * each claimed in-root source file (capped) goes through the SAME
 * runDiagnosticsForFile + formatDiagnostics pipeline the edit loop uses;
 * a non-empty formatted block is appended to the result value under
 * `diagnostics` so the model sees compiler feedback in the same turn.
 * Never changes a failed result; diagnostics failures are swallowed.
 */
function wrapWithExecuteDiagnostics<I extends Record<string, unknown>, O>(
  original: ToolDefinition<I, O>,
  root: string,
  runner?: Runner,
): ToolDefinition<I, O> {
  return {
    ...original,
    execute: async (rawArgs: I, ctx: ToolContext): Promise<TypedResult<O>> => {
      const result = await original.execute(rawArgs, ctx);
      if (!result.ok) return result;
      const value = result.value as Record<string, unknown> | null;
      if (!value || typeof value !== 'object') return result;
      try {
        const paths = claimedSourcePaths(
          original.name,
          rawArgs as Record<string, unknown>,
          root,
        ).slice(0, EXEC_DIAGNOSTICS_MAX_FILES);
        if (paths.length === 0) return result;
        const timeoutMs = Number(process.env.ZELARI_DIAGNOSTICS_TIMEOUT_MS) || 5000;
        const diags: Diagnostic[] = [];
        for (const p of paths) {
          diags.push(
            ...(await runDiagnosticsForFile(p, {
              cwd: root,
              timeoutMs,
              ...(runner ? { runner } : {}),
            })),
          );
        }
        const formatted = formatDiagnostics(diags, { relativeTo: root });
        if (formatted) {
          return { ok: true, value: { ...value, diagnostics: formatted } } as TypedResult<O>;
        }
      } catch {
        // Diagnostics must never break an execution — swallow and return as-is.
      }
      return result;
    },
  };
}

/** Audit-only wrap for tools with no path/shell args (network tools). */
function wrapWithAudit<I extends Record<string, unknown>, O>(
  original: ToolDefinition<I, O>,
  audit: AuditLogger,
  sessionId: string,
): ToolDefinition<I, O> {
  return {
    ...original,
    execute: async (rawArgs: I, ctx: ToolContext): Promise<TypedResult<O>> => {
      try {
        return await audit.runTool({
          tool: original.name,
          args: redactForAudit(rawArgs as Record<string, unknown>),
          sessionId,
          fn: () => original.execute(rawArgs, ctx),
        });
      } catch (err) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : String(err),
        } as TypedResult<O>;
      }
    },
  };
}

/**
 * P0.C2 (t17) evidence seam for exec_process: blocklist-style guard plus an
 * audit entry whose summary records program + argv + exitCode so executed
 * structured commands survive as verifiable evidence in the audit trail and
 * the session spine (tool events feed Completion Proof evidence refs).
 */
function wrapWithExecEvidence<I extends Record<string, unknown>, O>(
  original: ToolDefinition<I, O>,
  audit: AuditLogger,
  sessionId: string,
): ToolDefinition<I, O> {
  return {
    ...original,
    execute: async (rawArgs: I, ctx: ToolContext): Promise<TypedResult<O>> => {
      const args = rawArgs as Record<string, unknown>;
      const program = typeof args['program'] === 'string' ? args['program'] : '';
      const argv = Array.isArray(args['args'])
        ? args['args'].filter((x): x is string => typeof x === 'string')
        : [];
      const what = [program, ...argv].join(' ');
      try {
        return await audit.runTool({
          tool: original.name,
          args: redactForAudit(args),
          sessionId,
          summarize: (result) => {
            const r = result as TypedResult<{ exitCode?: number }>;
            const code = r.ok ? r.value.exitCode : undefined;
            // v2.17 (t28): carry the advisory os-jail signal into the audit
            // trail (visible fail-open must be auditable, not only on console).
            const jail = r.meta?.warnings
              ?.filter((w) => w.startsWith('os-jail:'))
              .join('; ');
            return `${what}${code !== undefined ? ` -> exitCode=${code}` : ''}${jail ? ` [${jail}]` : ''}`;
          },
          fn: () => original.execute(rawArgs, ctx),
        });
      } catch (err) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : String(err),
        } as TypedResult<O>;
      }
    },
  };
}

/**
 * Wrap the bash tool: assertShellAllowed() runs before execute(), the cwd
 * argument is jailed to the workspace root with resolveSandboxedPath()
 * (v2.17 t27 — the resolved ABSOLUTE cwd is injected so the child process
 * starts inside the sandbox; a cwd outside the root is a typed deny before
 * any process is created), and every invocation is audited.
 *
 * v2.17 (t28): OS-jail preflight when a JailSpec is provided —
 *  - ZELARI_OS_JAIL=required + backend missing ⇒ audited typed `[jail]`
 *    DENY before any spawn (golden rule: never warn/skip/upgrade);
 *  - ZELARI_OS_JAIL=advisory + backend missing ⇒ the command still runs
 *    (visible fail-open: the console signal is emitted by spawnJailed, the
 *    audit entry is appended here);
 *  - off / backend available ⇒ spawn proceeds through the osJail seam.
 */
function wrapWithShellSafety<I extends Record<string, unknown>, O>(
  original: ToolDefinition<I, O>,
  audit: AuditLogger,
  sessionId: string,
  root: string,
  jail?: JailSpec,
): ToolDefinition<I, O> {
  return {
    ...original,
    execute: async (rawArgs: I, ctx: ToolContext): Promise<TypedResult<O>> => {
      const args = rawArgs as Record<string, unknown>;
      const cmd = args['command'];
      if (typeof cmd === 'string') {
        try {
          assertShellAllowed(cmd);
        } catch (err) {
          if (err instanceof ShellBlockedError) {
            await audit.append({
              ts: new Date().toISOString(),
              sessionId,
              tool: original.name,
              args: redactForAudit(args),
              ok: false,
              resultSummary: err.message,
              durationMs: 0,
              error: 'shell_blocked',
            });
            return {
              ok: false,
              error: `[shell-blocked] ${err.message}`,
            } as TypedResult<O>;
          }
          throw err;
        }
        // Anti-self-kill (P0): refuse commands that would terminate the agent
        // host itself (node image kills / own process tree). Same audited
        // typed-error discipline as the blocklist — BEFORE any spawn.
        const selfKill = inspectCommand(cmd, { selfPid: process.pid, parentPid: process.ppid });
        if (selfKill.blocked && selfKill.message) {
          await audit.append({
            ts: new Date().toISOString(),
            sessionId,
            tool: original.name,
            args: redactForAudit(args),
            ok: false,
            resultSummary: selfKill.match ?? 'self_kill',
            durationMs: 0,
            error: 'self_kill_blocked',
          });
          return {
            ok: false,
            error: `[self-kill] ${selfKill.message}`,
          } as TypedResult<O>;
        }
      }
      // v2.17 (t27): jail the bash cwd to the workspace root BEFORE the
      // builtin spawns — the same discipline wrapWithSandbox applies to
      // filesystem path args (symlink-safe resolve). Absent cwd resolves to
      // the root itself, so the child never inherits an unjailed ctx.cwd.
      // Rejection happens BEFORE any spawn: the command does not run.
      const rawCwd = args['cwd'];
      try {
        args['cwd'] = resolveSandboxedPath(
          typeof rawCwd === 'string' && rawCwd.length > 0 ? rawCwd : '.',
          { root },
        );
      } catch (err) {
        if (!(err instanceof SandboxViolationError)) throw err;
        await audit.append({
          ts: new Date().toISOString(),
          sessionId,
          tool: original.name,
          args: redactForAudit(args),
          ok: false,
          resultSummary: err.message,
          durationMs: 0,
          error: 'sandbox_violation',
        });
        return {
          ok: false,
          error: `[sandbox] ${err.message}`,
        } as TypedResult<O>;
      }
      // v2.17 (t28): OS-jail preflight — BEFORE any spawn the mode and the
      // honest backend probe decide. required + missing ⇒ typed `[jail]`
      // deny (the builtin below never runs); advisory + missing ⇒ audited
      // visible fail-open and the spawn (unjailed) proceeds.
      if (jail) {
        const { mode, probe } = jailAvailability();
        if (mode !== 'off' && !probe.available) {
          if (mode === 'required') {
            const reason = jailDenyReason(probe, mode);
            await audit.append({
              ts: new Date().toISOString(),
              sessionId,
              tool: original.name,
              args: redactForAudit(args),
              ok: false,
              resultSummary: reason,
              durationMs: 0,
              error: 'jail_denied',
            });
            return { ok: false, error: `[jail] ${reason}` } as TypedResult<O>;
          }
          await audit.append({
            ts: new Date().toISOString(),
            sessionId,
            tool: original.name,
            args: redactForAudit(args),
            ok: true,
            resultSummary: `[os-jail] ${jailAdvisoryNotice(probe)}`,
            durationMs: 0,
          });
        }
      }
      try {
        return await audit.runTool({
          tool: original.name,
          args: redactForAudit(args),
          sessionId,
          fn: () => original.execute(rawArgs, ctx),
        });
      } catch (err) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : String(err),
        } as TypedResult<O>;
      }
    },
  };
}

function redactForAudit(args: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(args)) {
    if (/(api[_-]?key|secret|token|password)/i.test(k) && typeof v === 'string') {
      out[k] = '***';
    } else if (typeof v === 'string' && v.length > 200) {
      out[k] = v.slice(0, 200) + '…';
    } else {
      out[k] = v;
    }
  }
  return out;
}
